<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soProductionStepName"
        data-testid="so-production-steps-name-input"
        v-model="formData.so_production_step_name"
        :label="t('Nome da Etapa')"
        :placeholder="t('Digite o nome da etapa')"
        type="text"
        required
        :validation="validateName"
        fluid
      />
      <small v-if="errors.so_production_step_name" class="error-message">{{ errors.so_production_step_name }}</small>
    </div>
    <div class="form-field">
      <input-number
        elem-id="soProductionStepDuration"
        data-testid="so-production-steps-duration-input"
        v-model="formData.so_production_step_default_duration_minutes"
        :label="t('Duração Padrão (minutos)')"
        :placeholder="t('Minutos')"
        :min="0"
        fluid
      />
    </div>
    <div class="form-field">
      <label class="input-label">{{ t('QR Code') }}</label>
      <div style="display: flex; gap: 0.5rem; align-items: flex-start;">
        <input-qrcode
          elem-id="soProductionStepQrcode"
          data-testid="so-production-steps-qrcode-input"
          v-model="formData.so_production_step_qrcode"
          :label="''"
          :placeholder="t('Escaneie ou digite o QR Code')"
          style="flex: 1;"
        />
        <app-button
          type="button"
          :label="t('Gerar')"
          icon="pi pi-qrcode"
          size="small"
          severity="secondary"
          data-testid="so-production-steps-generate-qrcode-btn"
          @click="generateQrcode"
        />
      </div>
    </div>
    <div class="form-field">
      <input-rich-text
        elem-id="soProductionStepDescription"
        data-testid="so-production-steps-description-input"
        v-model="formData.so_production_step_description"
        :label="t('Descrição')"
        :placeholder="t('Digite a descrição da etapa')"
      />
    </div>
    <div class="form-field">
      <div style="display: flex; align-items: center; gap: 0.75rem;">
        <input-toggle
          elem-id="soProductionStepShowOnTv"
          data-testid="so-production-steps-show-on-tv-toggle"
          v-model="formData.so_production_step_show_on_tv"
        />
        <span class="toggle-label">{{ formData.so_production_step_show_on_tv ? t('Mostrar na Tela de TV') : t('Não mostrar na Tela de TV') }}</span>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  itemId?: Id | null;
  isEditMode?: boolean;
}

interface ProductionStepData {
  so_production_step_name?: string;
  so_production_step_qrcode?: string;
  so_production_step_description?: string;
  so_production_step_default_duration_minutes?: number | null;
  so_production_step_show_on_tv?: number | boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemId: null,
  isEditMode: false,
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

const { t } = useI18n();
const { getById, save } = useSOCatalog('production_steps');

const itemIdNumber = computed<number | null>(() => {
  if (!props.itemId) return null;
  return typeof props.itemId === 'string' ? parseInt(props.itemId, 10) : props.itemId;
});

const formData = reactive<{
  so_production_step_name: string;
  so_production_step_qrcode: string;
  so_production_step_description: string;
  so_production_step_default_duration_minutes: number | null;
  so_production_step_show_on_tv: boolean;
}>({
  so_production_step_name: '',
  so_production_step_qrcode: '',
  so_production_step_description: '',
  so_production_step_default_duration_minutes: null,
  so_production_step_show_on_tv: false,
});

const errors = reactive<{
  so_production_step_name: string;
}>({
  so_production_step_name: ''
});

const isLoading = ref<boolean>(false);

const validateName = (): boolean => {
  return !!formData.so_production_step_name && formData.so_production_step_name.trim() !== '' && formData.so_production_step_name.length <= 255;
};

const generateQrcode = (): void => {
  const timestamp = Date.now();
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let random = '';
  for (let i = 0; i < 4; i++) random += chars.charAt(Math.floor(Math.random() * chars.length));
  formData.so_production_step_qrcode = `STEP-${timestamp}-${random}`;
};

const loadForEdit = async (): Promise<void> => {
  if (!itemIdNumber.value) return;
  log('SOProductionStepsForm', 'loadForEdit', 'Loading for edit', itemIdNumber.value);
  isLoading.value = true;
  try {
    const data = await getById(itemIdNumber.value) as ProductionStepData | null;
    if (!data) throw new Error('Dados nao encontrados');
    formData.so_production_step_name = data.so_production_step_name || '';
    formData.so_production_step_qrcode = data.so_production_step_qrcode || '';
    formData.so_production_step_description = data.so_production_step_description || '';
    formData.so_production_step_description = data.so_production_step_description || '';
    formData.so_production_step_default_duration_minutes = data.so_production_step_default_duration_minutes || null;
    formData.so_production_step_show_on_tv = !!(data.so_production_step_show_on_tv ?? false);
    clearErrors();
  } catch (error: unknown) {
    logError('SOProductionStepsForm', 'loadForEdit', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar etapa de produção') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  clearErrors();
  let isValid = true;
  if (!formData.so_production_step_name || formData.so_production_step_name.trim() === '') {
    errors.so_production_step_name = t('Campo obrigatorio');
    isValid = false;
  }
  return isValid;
};

const clearErrors = (): void => { errors.so_production_step_name = ''; };

const resetForm = (): void => {
  formData.so_production_step_name = '';
  formData.so_production_step_qrcode = '';
  formData.so_production_step_description = '';
  formData.so_production_step_default_duration_minutes = null;
  formData.so_production_step_show_on_tv = false;
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) return;
  isLoading.value = true;
  try {
    const payload: {
      so_production_step_name: string;
      so_production_step_qrcode: string | null;
      so_production_step_description: string | null;
      so_production_step_default_duration_minutes: number | null;
      so_production_step_show_on_tv: number;
      so_production_step_id?: number;
    } = {
      so_production_step_name: formData.so_production_step_name.trim(),
      so_production_step_qrcode: formData.so_production_step_qrcode || null,
      so_production_step_description: formData.so_production_step_description || null,
      so_production_step_default_duration_minutes: formData.so_production_step_default_duration_minutes || null,
      so_production_step_show_on_tv: formData.so_production_step_show_on_tv ? 1 : 0,
    };
    if (props.isEditMode && itemIdNumber.value) {
      payload.so_production_step_id = itemIdNumber.value;
      await save(payload);
      eventBus.emit('globalMessage.success', t('Etapa de produção atualizada com sucesso'));
    } else {
      await save(payload);
      eventBus.emit('globalMessage.success', t('Etapa de produção criada com sucesso'));
    }
    emit('submit-success');
    resetForm();
  } catch (error: unknown) {
    logError('SOProductionStepsForm', 'handleSubmit', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar etapa de produção') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.itemId, async (newId) => {
  if (newId && props.isEditMode) { await nextTick(); await loadForEdit(); }
});

onMounted(() => {
  if (props.isEditMode && itemIdNumber.value) loadForEdit();
});

defineExpose({ loadForEdit, handleSubmit, resetForm, validateForm, getItemName: (): string => formData.so_production_step_name });
</script>

<style scoped>
.form-field { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
.error-message { color: var(--p-red-500); font-size: 0.875rem; margin-top: 0.25rem; display: block; }
</style>
